import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Fab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import { DataService } from 'src/services/data-service';
import SecurityDepositCreateDialog from './SecurityDepositCreateDialog';

interface SecurityDeposit {
  id: string;
  amount: number;
  paymentDate: string;
  lease: {
    id: string;
    refNumber: string;
    startDate: string;
    endDate: string;
    status: string;
    tenant: {
      id: string;
      fullName: string;
      emails: string[];
      phoneNumbers: string[];
    } | null;
    unit: {
      id: string;
      number: string;
    } | null;
  } | null;
  active: boolean;
}

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[4]
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

const SecurityDepositsListing: React.FC = () => {
  const [deposits, setDeposits] = useState<SecurityDeposit[]>([]);
  const [filteredDeposits, setFilteredDeposits] = useState<SecurityDeposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    fetchDeposits();
  }, []);

  useEffect(() => {
    filterDeposits();
  }, [searchTerm, deposits]);

  const fetchDeposits = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get('api/SecurityDeposit/all');

      if (response.ok) {
        const responseData = await response.json();
        let depositsList: SecurityDeposit[] = [];

        if (responseData.success && responseData.data) {
          depositsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          depositsList = responseData;
        }

        // Sort by payment date (newest first)
        depositsList.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());

        setDeposits(depositsList);
        setFilteredDeposits(depositsList);
      } else {
        throw new Error('Failed to fetch security deposits');
      }
    } catch (error) {
      console.error('Error fetching security deposits:', error);
      setError(error instanceof Error ? error.message : 'Failed to load security deposits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDeposits = () => {
    if (!searchTerm.trim()) {
      setFilteredDeposits(deposits);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = deposits.filter(deposit =>
      deposit.lease?.refNumber?.toLowerCase().includes(term) ||
      deposit.lease?.tenant?.fullName.toLowerCase().includes(term) ||
      deposit.lease?.unit?.number.toLowerCase().includes(term) ||
      deposit.amount.toString().includes(term) ||
      deposit.lease?.status?.toLowerCase().includes(term)
    );
    setFilteredDeposits(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLeaseStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' | 'info' => {
    const statusColors: Record<string, 'warning' | 'success' | 'error' | 'default' | 'info'> = {
      'Draft': 'warning',
      'Active': 'success',
      'Expired': 'error',
      'Terminated': 'default'
    };
    return statusColors[status] || 'default';
  };

  const calculateStats = () => {
    const totalAmount = filteredDeposits.reduce((sum, deposit) => sum + deposit.amount, 0);
    const activeDeposits = filteredDeposits.filter(d => d.active).length;
    const averageAmount = filteredDeposits.length > 0 ? totalAmount / filteredDeposits.length : 0;

    return {
      totalDeposits: filteredDeposits.length,
      totalAmount,
      activeDeposits,
      averageAmount
    };
  };

  const handleCreateSuccess = () => {
    fetchDeposits();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const stats = calculateStats();

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      {/* Page Header */}
      <PageHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold">
                Security Deposits
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5 }}>
                Manage all lease security deposits
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Add Deposit
          </Button>
        </Box>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 32, color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {stats.totalDeposits}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Deposits
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {stats.totalAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {stats.activeDeposits}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active Deposits
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary.main">
                    {stats.averageAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Average Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Search by tenant name, lease reference, unit number, amount, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Box>

      {/* Deposits Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            All Security Deposits ({filteredDeposits.length})
          </Typography>
        </Box>

        <TableContainer>
          {filteredDeposits.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 60, color: 'grey.300', mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {searchTerm ? 'No deposits found' : 'No security deposits available'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                {searchTerm ? 'Try adjusting your search criteria' : 'Security deposits will appear here once they are added to leases'}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Add First Deposit
                </Button>
              )}
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Lease Reference</strong></TableCell>
                  <TableCell><strong>Tenant</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Payment Date</strong></TableCell>
                  <TableCell><strong>Lease Status</strong></TableCell>
                  <TableCell><strong>Lease Period</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDeposits.map((deposit, index) => (
                  <TableRow key={deposit.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600" color="primary">
                        {deposit.lease?.refNumber || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {deposit.lease?.tenant?.fullName || 'N/A'}
                        </Typography>
                        {deposit.lease?.tenant?.emails && deposit.lease.tenant.emails.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {deposit.lease.tenant.emails[0]}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        Unit {deposit.lease?.unit?.number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {deposit.amount.toLocaleString()}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {formatDate(deposit.paymentDate)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {deposit.lease?.status ? (
                        <Chip
                          label={deposit.lease.status}
                          size="small"
                          color={getLeaseStatusColor(deposit.lease.status)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {deposit.lease?.startDate && deposit.lease?.endDate ? (
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatDate(deposit.lease.startDate)}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            to {formatDate(deposit.lease.endDate)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={deposit.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={deposit.active ? 'success' : 'default'}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        size="small"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' }
        }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Deposit Dialog */}
      <SecurityDepositCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Container>
  );
};

export default SecurityDepositsListing;
